import type { Vec2 } from './vec'
import type { Aabb } from './types'
import type { ContentId } from '../content/schema'
import type { SimState } from './state'

// A read-only copy of the render-relevant state, taken between ticks. Every
// field is deep-copied and the whole tree is deeply frozen, so the render
// adapter can hold it across frames and cannot write back into sim state.

export interface BuildingView {
  readonly id: ContentId
  readonly box: Aabb
  readonly inventory: Readonly<Record<ContentId, number>>
}

export interface UnitView {
  readonly id: number
  readonly pos: Vec2
  readonly cargo: ContentId | null
}

/** Read-only view of a resource patch, so render can draw the extractor's
 * source without reaching into sim state. */
export interface PatchView {
  readonly id: ContentId
  readonly box: Aabb
  readonly item: ContentId
}

/** Read-only view of the placement grid, so the render can draw a placement
 * affordance (ghost tile, occupied feedback) without reaching into sim state. */
export interface GridView {
  readonly tile: number
  readonly cols: number
  readonly rows: number
  readonly occupied: Readonly<Record<string, boolean>>
}

export interface RenderSnapshot {
  readonly tick: number
  readonly buildings: ReadonlyArray<BuildingView>
  readonly patches: ReadonlyArray<PatchView>
  readonly units: ReadonlyArray<UnitView>
  readonly grid: GridView
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const key of Object.keys(value as Record<string, unknown>)) {
      deepFreeze((value as Record<string, unknown>)[key])
    }
    Object.freeze(value)
  }
  return value
}

/**
 * Deep-copy the render-relevant state and freeze it. Call between ticks. The
 * result shares no reference with SimState.
 */
export function snapshot(state: SimState): RenderSnapshot {
  const buildings: BuildingView[] = state.buildings.map((b) => ({
    id: b.id,
    box: {
      min: { x: b.box.min.x, y: b.box.min.y },
      max: { x: b.box.max.x, y: b.box.max.y },
    },
    inventory: { ...b.inventory },
  }))

  const patches: PatchView[] = state.patches.map((p) => ({
    id: p.id,
    box: {
      min: { x: p.box.min.x, y: p.box.min.y },
      max: { x: p.box.max.x, y: p.box.max.y },
    },
    item: p.item,
  }))

  const units: UnitView[] = state.units.map((u) => ({
    id: u.id,
    pos: { x: u.pos.x, y: u.pos.y },
    cargo: u.cargo,
  }))

  const grid: GridView = {
    tile: state.grid.tile,
    cols: state.grid.cols,
    rows: state.grid.rows,
    occupied: { ...state.grid.occupied },
  }

  // `state.legs` is sim-only completion telemetry (tests + determinism payload);
  // nothing renders it, so it is deliberately NOT copied into the snapshot — a
  // per-frame deep-copy of a bounded-but-growing array would be pure waste.
  return deepFreeze({ tick: state.tick, buildings, patches, units, grid })
}
